/**
 * The error shape the backend answers with, and how to read it.
 *
 * <h2>One shape, every failure</h2>
 *
 * Every endpoint under `/api` returns `application/problem+json` on failure —
 * RFC 9457 — so the client has exactly one error format to understand rather
 * than one per screen. The one deliberate exception is `/oauth2/token`, which
 * follows RFC 6749 instead because every OAuth client library on earth expects
 * `error` and `error_description` there. {@link isOAuthError} covers that.
 */

/** The stable identifier a client should branch on. Never the title or the wording. */
export type ProblemType = string;

/**
 * The problem types this client branches on, spelled once.
 *
 * <p>Only the ones something actually reacts to differently. A type nothing
 * branches on does not belong here — the generic path already shows `detail`,
 * which is written to be read, and listing every type the backend can produce
 * would be a second copy of `ProblemTypes.java` to keep in step for no gain.
 */
export const PROBLEM_TYPES = {
  /**
   * A social sign-in refused because the provider account's address is personal.
   *
   * <p>The first entry in this map, added 16 October 2026, and it earns its place
   * by the rule above: {@link socialRefusalSentence} reacts to it differently,
   * replacing the server's sentence with one that names the provider the person
   * just came back from — which the server cannot do, because one endpoint serves
   * three providers and it does not know which button was pressed.
   *
   * <p>It arrives as the `error_uri` of an RFC 6749 refusal from the token
   * endpoint, not as an RFC 9457 `type`. A **blocked domain** deliberately does
   * not carry it and keeps `validation-failed` with registration's own wording.
   */
  personalEmailAddress: 'https://xtremand.com/problems/personal-email-address',

  /**
   * A social sign-in could not start because this deployment has no credentials
   * for that provider — no client id, no client secret, or no redirect URI.
   *
   * <p>Added 17 October 2026. {@link useStartSocialSignIn} reacts to it by naming
   * the provider and saying it is not configured *yet*, which is a different
   * sentence from every other failure: nobody pressing the button can fix it, and
   * it is not a fault in their account or their network.
   *
   * <p>**It arrives in two different fields, which is why
   * {@link ApiError.carries} exists.** Starting a sign-in is an RFC 9457 endpoint,
   * so it comes back as a problem document's `type`. The token endpoint answers
   * RFC 6749, so the same URI arrives there as `error_uri` beside an
   * `invalid_grant` — reachable when credentials are removed between the consent
   * screen opening and the code coming back.
   *
   * <p>Not every 503 means this. A 503 without this type is something else being
   * unavailable, and must not be reported as missing configuration.
   */
  socialProviderNotAvailable: 'https://xtremand.com/problems/social-provider-not-available',
} as const;

/**
 * One field-level complaint.
 *
 * `field` names the input the caller supplied — a JSON body property, or a path
 * variable when there is no body. It is never a database column, and mapping it
 * onto a form control is exactly what it is for.
 */
export interface FieldError {
  field: string;
  code: string;
  message: string;
}

/** An RFC 9457 problem, plus the two members this application adds. */
export interface ProblemDetail {
  type: ProblemType;
  title: string;
  status: number;
  detail: string;
  instance?: string;
  /** Present on this application's problems; correlates a report with the logs. */
  traceId?: string;
  /** Present when the failure is about specific inputs. */
  errors?: FieldError[];
  /**
   * RFC 9457 extension members, which a problem type may add and most do not.
   *
   * <p>`mail-account-not-verified` carries `protocol` (`SMTP` or `IMAP`) and `failure` (the
   * server's closed vocabulary — `AUTH_FAILED`, `CONNECT_REFUSED`, `TIMEOUT`, `TLS_FAILED`,
   * `HOST_NOT_ALLOWED`, `UNEXPECTED`). They are typed `unknown` rather than enumerated here
   * because every type's are different and a union of all of them would be a second place to
   * keep the server's shapes in step — a caller narrows what it reads at the point it reads
   * it. See `mailAccountNotVerified` in `problemTypes.ts`.
   */
  [extension: string]: unknown;
}

/** The RFC 6749 error body from `/oauth2/token`. A different standard, deliberately. */
export interface OAuthError {
  error: string;
  error_description?: string;
  /**
   * The problem type behind an RFC 6749 refusal, when the server sent one.
   *
   * <p>`invalid_grant` is returned for almost every way a sign-in can fail, and
   * deliberately so — it must not be usable to discover who has an account. That
   * makes `error` useless for branching, and this is the field that is not: the
   * token endpoint sets it to the same `https://xtremand.com/problems/…` URI an
   * RFC 9457 document would carry.
   *
   * <p>**It was declared nowhere and read nowhere until 16 October 2026**, so
   * every OAuth refusal arrived indistinguishable from every other and a client
   * that wanted to say something specific about one of them had only the sentence
   * to go on. Optional because most refusals deliberately send none.
   */
  error_uri?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Whether a response body is a problem document.
 *
 * <p>Checks the members that carry meaning rather than every member: a body with
 * a `type` and a numeric `status` is one, and being strict about the optional
 * ones would reject a valid problem the day the backend adds a member.
 */
export function isProblemDetail(value: unknown): value is ProblemDetail {
  return (
    isRecord(value) &&
    typeof value['type'] === 'string' &&
    typeof value['status'] === 'number' &&
    typeof value['title'] === 'string'
  );
}

export function isOAuthError(value: unknown): value is OAuthError {
  return isRecord(value) && typeof value['error'] === 'string';
}

/**
 * The field errors, or an empty list.
 *
 * <p>Empty rather than undefined so callers can iterate without a guard. A
 * problem with no `errors` is the normal shape for a failure that is not about
 * any particular input — a 409 on state, a 403 on permission.
 */
export function fieldErrorsOf(problem: ProblemDetail): FieldError[] {
  return problem.errors ?? [];
}

/**
 * The sentence to show a person.
 *
 * <p>`detail` is written for a reader and is what the backend intends to be
 * displayed. `title` is a short label fixed per problem type and reads as
 * jargon on its own ("Forbidden"), so it is the fallback rather than the choice.
 */
export function messageOf(problem: ProblemDetail): string {
  return problem.detail || problem.title;
}
